/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Action} from "../../models/action";
import {Event} from "../../models/event";
import {LocationInstance} from "../../models/location-instance";
import {Property} from "../../models/property";
import {PropertyType} from "../../models/property-type";
import {Rule} from "../../models/rule";
import {EventService} from "../../services/event.service";
import {RulesService} from "../../services/rules.service";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { RuleEditorStepAddFiltersComponent } from "./rule-editor-step-add-filters.component";
import { RuleEditorStepChooseReactionsComponent } from "./rule-editor-step-choose-reactions.component";
import { RuleEditorStepReviewAndFinishComponent } from "./rule-editor-step-review-and-finish.component";
import { RuleEditorStepSelectTriggerComponent } from "./rule-editor-step-select-trigger.component";
import { RuleEditorStepSetupParametersComponent } from "./rule-editor-step-setup-parameters.component";
import {OnInit, Component, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";

declare var $: any;

@Component({
  templateUrl: './rule-editor.component.html'
})
export class RuleEditorComponent implements OnInit {
  
  //UI Elements used for the Rule Editor Steps
  @ViewChild(RuleEditorStepSelectTriggerComponent)
  mRuleEditorStepSelectTriggerComponent: RuleEditorStepSelectTriggerComponent;
  
  @ViewChild(RuleEditorStepSetupParametersComponent)
  mRuleEditorStepSetupParametersComponent: RuleEditorStepSetupParametersComponent;
  
  @ViewChild(RuleEditorStepAddFiltersComponent)
  mRuleEditorStepAddFiltersComponent: RuleEditorStepAddFiltersComponent;
  
  @ViewChild(RuleEditorStepChooseReactionsComponent)
  mRuleEditorStepChooseReactionsComponent: RuleEditorStepChooseReactionsComponent;
  
  @ViewChild(RuleEditorStepReviewAndFinishComponent)
  mRuleEditorStepReviewAndFinishComponent: RuleEditorStepReviewAndFinishComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Rule being shown in the UI, either passed in by the resolver to be edited or created for a new one
  mRule: Rule;
  
  //Current Step that is being displayed to the User
  mStep = Step.S1_SELECT_TRIGGER;

  mLocations: LocationInstance[];
    
  constructor(
    private mRoute: ActivatedRoute
  ) {}

  
  ngOnInit(): void {
    this.mRoute.data.subscribe((data: {rule: Rule, events: Event[], actions: Action[], locations: LocationInstance[]}) => {
      console.log("Editing Rule: " + (data.rule == null ? "*NULL*" : (data.rule.id + "/" + data.rule.name)));
      if (data.rule == null) {
        const newRule = new Rule();
        newRule.conditions = [];
        newRule.isActive = true;
        this.mRule = newRule;
      } else {
        this.mRule = data.rule;
      }
    });
      
    if (this.mRule.locations != null && this.mRule.locations.length == 0) {
        this.mRule.locations = null;
    }

    if (this.mRule.zones != null && this.mRule.zones.length == 0) {
        this.mRule.zones = null;
    }
  }

  
  selectStep(step: Step): void {
    if (step != null && this.mStep != step) {
      console.log("Step Change! " + this.mStep + " => " + step);

      //Hide the old Step...
      this.setCurrentStepVisible(false);

      //Set the current Step
      this.mStep = step;

      //Show the new Step...
      this.setCurrentStepVisible(true);
    }
  }
  
  private setCurrentStepVisible(visible: boolean): void {
    switch (this.mStep) {
      case Step.S1_SELECT_TRIGGER: {
        return this.mRuleEditorStepSelectTriggerComponent.setVisible(visible);
      }
      case Step.S2_SET_UP_PARAMETERS: {
        return this.mRuleEditorStepSetupParametersComponent.setVisible(visible);
      }
      case Step.S3_ADD_FILTERS: {
        return this.mRuleEditorStepAddFiltersComponent.setVisible(visible);
      }
      case Step.S4_CHOOSE_REACTIONS: {
        return this.mRuleEditorStepChooseReactionsComponent.setVisible(visible);
      }
      case Step.S5_REVIEW_AND_FINISH: {
        return this.mRuleEditorStepReviewAndFinishComponent.setVisible(visible);
      }
    }
  }

  
  /*
   * Generic Error Handling method
   * Displays error in an alert
   */
  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}

export enum Step {
  S1_SELECT_TRIGGER = "STEP 1 - Select Trigger",
  S2_SET_UP_PARAMETERS = "STEP 2 - Set Up Parameters",
  S3_ADD_FILTERS = "STEP 3 - Add Filters",
  S4_CHOOSE_REACTIONS = "STEP 4 - Choose Reactions",
  S5_REVIEW_AND_FINISH = "STEP 5 - Review & Finish"
}
